import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
} from "recharts";
import { ChartContainer } from "@shared/components/ui/chart";
import { formatPackageStatus } from "@shared/utils/helper";
import { useTranslation } from "react-i18next";
import { CHART_STATUS_COLORS } from "@shared/utils/constants";
import { isRTL, getDirection } from "@shared/utils/rtl";

export const description =
  "A modern bar chart with enhanced UI design and accessibility features";

export function ChartBarLabel({ data }) {
  const { t, i18n } = useTranslation();
  const isRTLLanguage = isRTL(i18n.language);
  const direction = getDirection(i18n.language);

  // Enhanced chart data with better color mapping
  const chartData = formatPackageStatus(data).map((item) => ({
    label: t(`status.${item.label}`),
    value: item.value,
    status: item.label,
    colorClass: CHART_STATUS_COLORS[item.label] || "fill-gray-400",
  }));

  const chartConfig = {
    value: {
      label: t("packages.packages"),
      color: "hsl(var(--chart-1))",
    },
  };

  // Enhanced custom tick component with better styling
  const CustomTick = ({ x, y, payload }) => {
    const maxLength = 12; // Maximum characters before truncation
    const displayValue =
      payload.value.length > maxLength
        ? `${payload.value.substring(0, maxLength)}...`
        : payload.value;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="currentColor"
          fontSize="12"
          className="text-muted-foreground font-medium"
          style={{ direction: direction }}
        >
          {displayValue}
        </text>
      </g>
    );
  };

  // Custom tooltip for better UX
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-lg p-3 min-w-[120px]">
          <p className="font-medium text-sm mb-1">{label}</p>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: data.color }}
            />
            <span className="text-lg font-bold">{data.value}</span>
            <span className="text-sm text-muted-foreground">
              {t("packages.packages")}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] md:h-[400px] text-muted-foreground">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <svg
              className="w-8 h-8 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium">{t("dashboard.noChartData")}</h3>
          <p className="text-xs text-muted-foreground max-w-[200px]">
            {t("dashboard.noChartDataDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" dir={direction}>
      <ChartContainer
        config={chartConfig}
        className={`h-[400px] md:h-[400px] w-full ${
          isRTLLanguage ? "rtl" : "ltr"
        }`}
      >
        <BarChart
          accessibilityLayer
          data={chartData}
          margin={{
            top: 24,
            right: isRTLLanguage ? 32 : 24,
            left: isRTLLanguage ? 24 : 32,
            bottom: 2,
          }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            className="stroke-muted opacity-50"
          />

          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={<CustomTick />}
            height={80}
            interval={0}
            className="text-muted-foreground"
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            className="text-muted-foreground text-xs"
            width={40}
          />

          <Tooltip content={<CustomTooltip />} />

          <Bar
            dataKey="value"
            radius={[8, 8, 0, 0]}
            className="transition-all duration-200 hover:opacity-80"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                className={`${entry.colorClass} transition-all duration-200 hover:brightness-110`}
              />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              className="text-xs fill-foreground font-semibold"
              offset={12}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
}
